import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getJson, PRC_PREFIX } from "./client";
import type { ConcentrationRow } from "./types";

/**
 * Joints — combinations of limits that bite the same contracts.
 *
 * A joint's headroom is min() over its members' own headrooms, because the same new
 * dollars have to satisfy every member constraint at once. It is NOT a sum: per-limit
 * headrooms overlap rather than partition the facility, which is the whole reason this
 * page exists.
 *
 * Creating, editing and archiving a joint goes through POST /proposals (see
 * limit-config.ts), not through any endpoint here — joints are governed exactly like
 * every other limit change. This module is read-only apart from candidate dismissal.
 */

export type JointLifecycle = "ACTIVE" | "INACTIVE";

export interface JointMemberDef {
  memberRef: string;
  memberKind: "internal" | "external";
  limitDesc: string;
  sortOrder: number;
}

export interface JointDef {
  jointId: string;
  jointName: string;
  facilityType: "internal" | "external";
  facilityCode: string;
  contractType: string | null;
  categoryGroup: string;
  rationale: string | null;
  alertDistToThresholdPct: number | null;
  supersetReportId: number | null;
  effectiveStartDate: string | null;
  status: JointLifecycle;
  createdFromProposalId: string | null;
  createdByRole: string | null;
  pendingProposalId: string | null;
  members: JointMemberDef[];
}

export interface FacilityCapacitySummary {
  facilityCode: string;
  /** External: the facility commitment. Internal: the fixed capital base K. */
  totalCommitmentDollars: number;
  /** D — the denominator the percentage limits divide by. */
  drawnDollars: number;
  /** Floored at zero — check isOverCommitted before reading a zero as "exactly full". */
  remainingFacilityDollars: number;
  /** True when the drawn book runs past the commitment. Currently the case for CAP 1. */
  isOverCommitted: boolean;
  overCommittedDollars: number;
  /** True for INTERNAL: K is fixed, so headroom is linear. */
  isFixedBase: boolean;
  /** True while no sourced commitment figure exists — the UI must say so. */
  isPlaceholder: boolean;
  sourceNote: string | null;
}

export interface JointsResponse {
  facilityCode: string;
  asOfDate: string;
  capacity: FacilityCapacitySummary;
  aggregateFlag: { fired: boolean; flaggedCount: number; flaggedLimits: string[] };
  /** Joint rows, in the same shape as any other concentration row. */
  rows: ConcentrationRow[];
  joints: ConcentrationRow[];
  /** The single-limit rows the joints are derived from, tightest first. */
  limits: ConcentrationRow[];
  /**
   * Deliberately much larger than remainingFacilityDollars. Limits overlap; they do not
   * partition the facility. That gap is the page's argument, not a discrepancy.
   */
  sumOfHeadroomsDollars: number;
  notes: string[];
}

export interface JointCandidate {
  candidateId: string;
  facilityCode: string;
  suggestedName: string;
  memberRefs: string[];
  limitDescs: string[];
  overlapNiDollars: number;
  overlapContracts: number;
  /** Overlap as a share of the SMALLER member — the primary ranking key. */
  containment: number;
  jaccard: number;
  /** One limit is a subset of the other by construction; ranked last. */
  isNested: boolean;
  memberNiDollars: number[];
  memberContracts: number[];
  projectedJointHeadroomDollars: number | null;
  projectedBindingMemberRef: string | null;
  overlapSentence: string;
  rank: number;
}

export interface JointCandidatesResponse {
  facilityCode: string;
  fileDate: string | null;
  size: number;
  basis: string;
  dayTotalNiDollars: number | null;
  consideredLimits: number;
  candidates: JointCandidate[];
  excludedLimits: { memberRef: string; limitDesc?: string; reason: string }[];
  notes: string[];
}

export interface JointDetailResponse {
  joint: JointDef;
  row: ConcentrationRow;
  memberTrends: { memberRef: string; limitDesc: string; trend: { date: string; dollarPct: number }[] }[];
  asOfDate: string;
}

const dismissedCandidates = new Set<string>();
const jointListeners = new Set<() => void>();
const notifyJoints = () => jointListeners.forEach((l) => l());

export function subscribeJoints(l: () => void): () => void {
  jointListeners.add(l);
  return () => {
    jointListeners.delete(l);
  };
}

// ═══════════════════════════════════════════════════════════════════════════
//  Fetchers + hooks
// ═══════════════════════════════════════════════════════════════════════════
export async function fetchJoints(facilityCode: string, asOfDate: string): Promise<JointsResponse> {
  const params = new URLSearchParams({ facility: facilityCode, as_of: asOfDate });
  return getJson<JointsResponse>(`${PRC_PREFIX}joints/headroom?${params.toString()}`);
}

export function useJoints(facilityCode: string, asOfDate: string) {
  return useQuery({
    queryKey: ["prc-joints", facilityCode, asOfDate],
    queryFn: () => fetchJoints(facilityCode, asOfDate),
    enabled: !!facilityCode,
  });
}

export async function fetchJointDefs(facilityCode?: string): Promise<JointDef[]> {
  const q = facilityCode ? `?facility=${facilityCode}` : "";
  return getJson<JointDef[]>(`${PRC_PREFIX}joints${q}`);
}

export function useJointDefs(facilityCode?: string) {
  return useQuery({
    queryKey: ["prc-joint-defs", facilityCode ?? "all"],
    queryFn: () => fetchJointDefs(facilityCode),
  });
}

export async function fetchJointCandidates(
  facilityCode: string,
  size = 2,
): Promise<JointCandidatesResponse> {
  const params = new URLSearchParams({ facility: facilityCode, size: String(size) });
  return getJson<JointCandidatesResponse>(`${PRC_PREFIX}joint-candidates?${params.toString()}`);
}

export function useJointCandidates(facilityCode: string, size = 2) {
  return useQuery({
    queryKey: ["prc-joint-candidates", facilityCode, size],
    queryFn: () => fetchJointCandidates(facilityCode, size),
    enabled: !!facilityCode,
  });
}

export function useDismissJointCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (candidateId: string) => {
      // Dismissal is local-only for now: there is no server-side dismissal store, and
      // inventing one would need its own governance question (who un-dismisses?).
      dismissedCandidates.add(candidateId);
      notifyJoints();
      return candidateId;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prc-joint-candidates"] });
    },
  });
}

export async function fetchJointDetail(jointId: string, asOfDate: string): Promise<JointDetailResponse> {
  return getJson<JointDetailResponse>(
    `${PRC_PREFIX}joints/${encodeURIComponent(jointId)}/headroom?as_of=${asOfDate}`,
  );
}
